import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  EnrichedEntryStatus,
  MakerFlag
} from '../../types/entry.types';
import {
  useSetPairOpenerEnabled,
  useBulkSetPairOpenerEnabled,
} from '../../api/useEntry';
import { usePositionsGrouped } from '../../api/usePositions';
import { BulkActionBar } from '../BulkActionBar';
import {
  KALSHI_TAKER_FEE_RATE,
  polymarketTakerFeeRate,
  takerTnaFromSum,
  fmtPrice,
  fmtQty,
  fmtPct,
  TAB_FLAGS,
  FLAG_TO_SIDES
} from './entryMath';

interface PairContracts {
  polyYes: number;
  polyNo: number;
  kalshiYes: number;
  kalshiNo: number;
}

const ZERO_CONTRACTS: PairContracts = {
  polyYes: 0,
  polyNo: 0,
  kalshiYes: 0,
  kalshiNo: 0,
};

const fmtContracts = (v: number): string => {
  if (!v) return '—';
  return Math.round(v).toString();
};

/**
 * Per-input save affordance shared by Threshold / Size / MaxV cells.
 * Renders nothing when the field is clean and not mid-save so the cell
 * stays compact at rest. Each input gets its own copy so the operator
 * sees the tick next to the exact field they edited.
 */
function renderSaveIndicator(
  saving: boolean,
  dirty: boolean,
  onClick: () => void,
): JSX.Element | null {
  if (saving && dirty) return <CircularProgress size={12} sx={{ ml: 0.2 }} />;
  if (saving) return null;
  if (!dirty) return null;
  return (
    <IconButton
      size="small"
      onClick={onClick}
      aria-label="save"
      sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
    >
      <CheckIcon sx={{ fontSize: '0.8rem' }} />
    </IconButton>
  );
}

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.4,
  px: 0.1,
  lineHeight: 1.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};

// Sticky offset for the second header row. Must equal row 1's actual
// rendered height in pixels, otherwise the second row either covers
// part of row 1 (overlap) or floats over the body (gap → first body
// row scrolls behind it). With `py: 0.4` + fontSize 0.58rem +
// lineHeight 1.2 + 1px border the row is ~21px.
const ROW1_HEIGHT = 21;

// Second-header-row cells get extra bottom padding + a stronger
// divider so there's visible breathing room between the sub-header
// and the first body row. Bump pb if the operator reports the body
// row 1 still looks crowded against the sub-header.
const subHSx = {
  pb: 1.2,
  borderBottom: '2px solid rgba(255,255,255,0.18)',
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.05 } as const;

export interface CreateEntrySumRuleParams {
  pair: ManualConfirmedPair;
  /**
   * Which of the 4 maker flags this row activated on. Drives the initial
   * path. Null when activation came from TT or a pre-rule paused-create
   * via the param ✓.
   */
  makerFlag: MakerFlag | null;
  /**
   * Optional initial strategy. Defaults to 'maker-first' on the backend.
   * Set to 'taker-taker' when the row activates via the TT switch with
   * no maker flag set.
   */
  entryStrategy?: 'maker-first' | 'taker-taker';
  /**
   * Path of the tab the activation happened in. Drives the rule's poly /
   * kalshi sides when `makerFlag` is null (TT-only or paused-create from
   * params). Ignored when `makerFlag` is set — sides derive from the flag
   * via FLAG_TO_SIDES.
   */
  tabPath: 'a' | 'b';
  thresholdSum: number;
  size: number;
  maxVolumeContracts: number;
}

interface Props {
  /**
   * Which entry path this tab governs. Tab A = poly YES + kalshi NO;
   * Tab B = poly NO + kalshi YES.
   */
  path: 'a' | 'b';
  pairs: ManualConfirmedPair[];
  rules: EntryRule[] | undefined;
  anyRulesByPairId: Map<string, EntryRule>;
  getState: (id: string) => EnrichedEntryStatus | undefined;
  /**
   * Pair IDs that have an ACTIVE rule on the OTHER path (or contracts
   * held on the other path). All 3 switches on those rows are disabled
   * here.
   */
  lockedPairIds: Set<string>;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[],
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

// Proportional column widths fed to a <colgroup>. Same pattern as the
// unwinder's Sum-of-Sides table (see `UnwindingTabSumOfSides.tsx`):
// `tableLayout: 'fixed'` + per-cell pixel widths sums to ~1324 px and
// the browser refuses to stretch the table to fill a wider container.
// Listing the widths once as percentages of the total gives every
// column its proportional share of 100% of the table width. Order
// matches the rendered column sequence: checkbox · Market · YES · NO ·
// Polymarket (Qty/Bid/Ask/Qty) · Kalshi (Qty/Bid/Ask/Qty) · Parameters
// (Thr/Size/MaxV) · 3 strategy groups (Legs + $/% each) · Ctrl (P/K/TT).
const COL_WIDTHS_PX = [
  18, 480, 56, 56,
  24, 32, 32, 24,
  24, 32, 32, 24,
  40, 30, 36,
  50, 50, 50, 50, 50, 50,
  28, 28, 28,
] as const;
const COL_TOTAL_PX = COL_WIDTHS_PX.reduce((s, w) => s + w, 0);

type SortKey =
  | 'name'
  | 'sumTT'
  | 'tnaTT'
  | 'sumPolyMk'
  | 'tnaPolyMk'
  | 'sumKalshiMk'
  | 'tnaKalshiMk';
type SortDir = 'asc' | 'desc';

// Sums sort ascending by default (cheapest combined cost first); TNAs
// sort descending by default (highest annualized return first).
const SUM_KEYS: SortKey[] = ['sumTT', 'sumPolyMk', 'sumKalshiMk'];

/* ------------------------------------------------------------------ */
/* Math helpers — per-pair                                            */
/* ------------------------------------------------------------------ */

interface PathMetrics {
  polyAsk: number | null;
  polyBid: number | null;
  kalshiAsk: number | null;
  kalshiBid: number | null;
  polyAllIn: number | null;
  kalshiAllIn: number | null;
  polyMakerRest: number | null;
  kalshiMakerRest: number | null;
  sumTT: number | null;
  sumPolyMk: number | null;
  sumKalshiMk: number | null;
  tnaTT: number | null;
  tnaPolyMk: number | null;
  tnaKalshiMk: number | null;
}

const computePathMetrics = (
  path: 'a' | 'b',
  liveState: EnrichedEntryStatus | undefined,
): PathMetrics => {
  // Raw top-of-book bid/ask for the tab's path (rendered in the
  // platform Bid/Ask cells). Backend WS quotes from the MarketState.
  const polyAsk =
    (path === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk)?.price ?? null;
  const polyBid =
    (path === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid)?.price ?? null;
  const kalshiAsk =
    (path === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk)?.price ?? null;
  const kalshiBid =
    (path === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid)?.price ?? null;

  // Strategy sums, maker rest, and all-in taker prices ALL come from the
  // backend `engineSim` payload — the SAME helpers the engine triggers
  // orders on (depth-walked VWAPs + findBestMakerPrice). There is NO
  // client-side fallback: the FE must never approximate these numbers.
  // When `engineSim` is absent (backend has no orderbook for the pair)
  // every sim field is null and the cell renders "—".
  const engine = liveState?.engineSim?.[path];
  const polyAllIn = engine?.polyTakerAllInVwap ?? null;
  const kalshiAllIn = engine?.kalshiTakerAllInVwap ?? null;
  const polyMakerRest = engine?.polyMakerRest ?? null;
  const kalshiMakerRest = engine?.kalshiMakerRest ?? null;
  const sumTT = engine?.sumTT ?? null;
  const sumPolyMk = engine?.sumPolyMk ?? null;
  const sumKalshiMk = engine?.sumKalshiMk ?? null;

  const days = liveState?.daysToExpiry ?? null;
  return {
    polyAsk,
    polyBid,
    kalshiAsk,
    kalshiBid,
    polyAllIn,
    kalshiAllIn,
    polyMakerRest,
    kalshiMakerRest,
    sumTT,
    sumPolyMk,
    sumKalshiMk,
    tnaTT: takerTnaFromSum(sumTT, days),
    tnaPolyMk: takerTnaFromSum(sumPolyMk, days),
    tnaKalshiMk: takerTnaFromSum(sumKalshiMk, days),
  };
};

/* ------------------------------------------------------------------ */
/* Table                                                              */
/* ------------------------------------------------------------------ */

export const EntryTabByPath = ({
  path,
  pairs,
  rules,
  anyRulesByPairId,
  getState,
  lockedPairIds,
  onCreateRule,
  onSetMakerFlag,
  onUpdateRule,
  onHidePair,
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('tnaTT');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Freeze-sort: when on, the row order is held at whatever it was when
  // frozen so live price ticks stop reshuffling the table. New pairs
  // that appear while frozen are appended at the end; a sort-column
  // change re-snapshots once and stays frozen. `frozenOrder` is the
  // snapshotted list of pair ids — kept in state (not a ref) so a
  // re-snapshot re-renders immediately instead of waiting for a tick.
  const [frozen, setFrozen] = useState(false);
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  const rulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      map.set(rule.pairId, rule);
    }
    return map;
  }, [rules]);

  // Current open contracts split by (platform × side) per pair.
  // Mirrors the unwinder (UnwindingPage.tsx) which uses the same REST
  // hook; the /opening route is NOT wrapped by PositionsProvider, so
  // the context-based usePositions() throws here. 10s poll matches the
  // unwinder's freshness budget.
  const { data: positionsData } = usePositionsGrouped({ status: 'open' });
  const contractsByPairId = useMemo(() => {
    const map = new Map<string, PairContracts>();
    const groups = positionsData?.groups ?? [];
    for (const g of groups) {
      if (!g._id) continue;
      let polyYes = 0;
      let polyNo = 0;
      let kalshiYes = 0;
      let kalshiNo = 0;
      for (const p of g.positions) {
        if (p.status !== 'open' || !p.contracts) continue;
        if (p.platform === 'polymarket') {
          if (p.side === 'yes') polyYes += p.contracts;
          else polyNo += p.contracts;
        } else if (p.platform === 'kalshi') {
          if (p.side === 'yes') kalshiYes += p.contracts;
          else kalshiNo += p.contracts;
        }
      }
      map.set(g._id, { polyYes, polyNo, kalshiYes, kalshiNo });
    }
    return map;
  }, [positionsData]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        // Sums: ascending default (cheapest first). TNAs: descending
        // default (highest yield first). Name: ascending.
        if (key === 'name') setSortDir('asc');
        else if (SUM_KEYS.includes(key)) setSortDir('asc');
        else setSortDir('desc');
      }
    },
    [sortKey],
  );

  // Compute per-pair sort metric. Each sort key maps to ONE specific
  // sum or TNA value from the metrics — no more cross-strategy min/max.
  const sortedPairs = useMemo(() => {
    const annotated = pairs.map(pair => {
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      const liveState =
        (pair._id ? getState(pair._id) : undefined) ??
        (rule ? getState(rule._id) : undefined);
      const m = computePathMetrics(path, liveState);
      const hasSim =
        m.sumTT != null || m.sumPolyMk != null || m.sumKalshiMk != null;
      let metric: number | null = null;
      switch (sortKey) {
        case 'sumTT': metric = m.sumTT; break;
        case 'tnaTT': metric = m.tnaTT; break;
        case 'sumPolyMk': metric = m.sumPolyMk; break;
        case 'tnaPolyMk': metric = m.tnaPolyMk; break;
        case 'sumKalshiMk': metric = m.sumKalshiMk; break;
        case 'tnaKalshiMk': metric = m.tnaKalshiMk; break;
        default: metric = null;
      }
      return { pair, metric, hasSim };
    });
    annotated.sort((a, b) => {
      if (sortKey === 'name') {
        const an = a.pair.pairName ?? '';
        const bn = b.pair.pairName ?? '';
        return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      }
      if (a.hasSim !== b.hasSim) return a.hasSim ? -1 : 1;
      if (a.metric == null && b.metric == null) return 0;
      if (a.metric == null) return 1;
      if (b.metric == null) return -1;
      return sortDir === 'desc' ? b.metric - a.metric : a.metric - b.metric;
    });
    return annotated.map(x => x.pair);
  }, [pairs, rulesByPairId, getState, sortKey, sortDir, path]);

  // Snapshot the live order whenever the freeze turns on or the sort
  // target changes while frozen. Deliberately NOT keyed on sortedPairs —
  // a live WS tick must never re-snapshot, that's the whole point.
  useEffect(() => {
    if (!frozen) {
      setFrozenOrder(null);
      return;
    }
    setFrozenOrder(sortedPairs.map(p => p._id ?? p.pairName ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frozen, sortKey, sortDir]);

  // Rows actually rendered. When frozen, reorder the live-sorted list to
  // the frozen rank; pairs without a frozen rank (added after the freeze)
  // fall to the end keeping their live-sorted order (Array.sort is stable).
  const displayedPairs = useMemo(() => {
    if (!frozen || !frozenOrder) return sortedPairs;
    const rank = new Map<string, number>();
    frozenOrder.forEach((id, i) => rank.set(id, i));
    return [...sortedPairs].sort((a, b) => {
      const ra = rank.get(a._id ?? a.pairName ?? '') ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b._id ?? b.pairName ?? '') ?? Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
  }, [frozen, frozenOrder, sortedPairs]);

  /* ---------------------------------------------------------------- */
  /* Bulk market-data ON/OFF — multi-select + master toggle           */
  /* ---------------------------------------------------------------- */

  // Checked pair ids. Selection is per-tab state: it resets on tab
  // switch, which is fine — the locked-pair set is per-tab too, so a
  // selection never carries a pair the active tab can't manage.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkOpener = useBulkSetPairOpenerEnabled();

  // Rows whose checkbox is actually actionable: have an id and are not
  // locked by an active rule on the other path.
  const selectableIds = useMemo(
    () =>
      displayedPairs
        .map(p => p._id)
        .filter((id): id is string => !!id && !lockedPairIds.has(id)),
    [displayedPairs, lockedPairIds],
  );

  // ─── Row virtualization (2026-05-23) ──────────────────────────────────
  // Mount only the visible `EntryRowByPath` instances + a top/bottom
  // spacer. Without this, hundreds of MUI `<TableRow>`s (each ~22
  // `<TableCell>` plus per-cell `sx={...}` + `<TextField>` / `<Switch>`
  // / `<Tooltip>`) were the dominant retained-memory cost — DOM nodes
  // grew linearly with the manual-confirmed-pair count and Emotion's
  // style cache piled up the per-row `sx` objects forever.
  //
  // EntryRowByPath has no expanded-row variant, so a fixed `estimateSize`
  // is enough — measureElement isn't needed (the unwinder's TNA tab,
  // which DOES have an expandable `SimulationBreakdownPanel`, wires
  // `measureElement` separately).
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayedPairs.length,
    getScrollElement: () => tableContainerRef.current,
    // ~52px per row at default density — measured from a populated tab.
    estimateSize: () => 52,
    overscan: 8,
  });
  const allSelected =
    selectableIds.length > 0 && selected.size >= selectableIds.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected(prev =>
      prev.size >= selectableIds.length && selectableIds.length > 0
        ? new Set()
        : new Set(selectableIds),
    );
  }, [selectableIds]);

  // scope:'ids' — flip the checked subset. Backend caps pairIds at 100
  // per request, so chunk larger selections.
  const applyBulkSelected = useCallback(
    async (enabled: boolean) => {
      const ids = [...selected];
      if (ids.length === 0) return;
      setBulkBusy(true);
      try {
        for (let i = 0; i < ids.length; i += 100) {
          await bulkOpener.mutateAsync({
            scope: 'ids',
            pairIds: ids.slice(i, i + 100),
            enabled,
          });
        }
        setSelected(new Set());
      } finally {
        setBulkBusy(false);
      }
    },
    [selected, bulkOpener],
  );

  // scope:'all' — flip every opener-eligible pair server-side in one
  // request (no id list to ship, no 100-cap to chunk around).
  const applyBulkAll = useCallback(
    async (enabled: boolean) => {
      setBulkBusy(true);
      try {
        await bulkOpener.mutateAsync({ scope: 'all', enabled });
        setSelected(new Set());
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkOpener],
  );

  if (pairs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No manually confirmed pairs found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' },
    // Show the direction arrow on EVERY sortable label, even when
    // inactive — dimmed at 40% so the active sort still pops. Lets
    // the operator see all sortable handles at a glance instead of
    // only the currently-active one.
    '&:not(.Mui-active) .MuiTableSortLabel-icon': {
      opacity: 0.4,
    },
  } as const;

  // Per-column $ / % badge. Active target gets a light blue background +
  // matching text so the operator can tell which of the two is the sort
  // key at a glance — replaces the per-label arrows the column used to
  // render.
  const sortBadgeSx = (active: boolean) =>
    ({
      cursor: 'pointer',
      px: 0.4,
      borderRadius: 0.5,
      color: active ? '#42a5f5' : 'inherit',
      backgroundColor: active ? 'rgba(66, 165, 245, 0.18)' : 'transparent',
      '&:hover': { color: '#42a5f5' },
    }) as const;

  const polySideLabel = path === 'a' ? 'YES' : 'NO';
  const kalshiSideLabel = path === 'a' ? 'NO' : 'YES';

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <BulkActionBar
        totalCount={selectableIds.length}
        selectedCount={selected.size}
        busy={bulkBusy}
        onEnableSelected={() => void applyBulkSelected(true)}
        onDisableSelected={() => void applyBulkSelected(false)}
        onClearSelection={() => setSelected(new Set())}
        onEnableAll={() => void applyBulkAll(true)}
        onDisableAll={() => void applyBulkAll(false)}
      />
      <TableContainer
        ref={tableContainerRef}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {COL_WIDTHS_PX.map((w, i) => (
            <col key={i} style={{ width: `${(w / COL_TOTAL_PX) * 100}%` }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ ...gSx, width: 18, px: 0, verticalAlign: 'middle', top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
              align="center"
              padding="none"
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
                disabled={selectableIds.length === 0 || bulkBusy}
                sx={{ p: 0, color: 'grey.500', '& .MuiSvgIcon-root': { fontSize: 14 } }}
              />
            </TableCell>
            <TableCell
              sx={{ ...gSx, width: 480, top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
              >
                <TableSortLabel
                  active={sortKey === 'name'}
                  direction={sortKey === 'name' ? sortDir : 'asc'}
                  onClick={() => handleSortClick('name')}
                  sx={sortLabelSx}
                >
                  Market
                </TableSortLabel>
                <Tooltip
                  title={
                    frozen
                      ? 'Sort frozen — row order is held. Click to resume live sorting.'
                      : 'Freeze sort — hold the current row order so it stops reshuffling on price updates.'
                  }
                  placement="top"
                >
                  <IconButton
                    size="small"
                    onClick={() => setFrozen(f => !f)}
                    aria-label={frozen ? 'resume live sorting' : 'freeze sort'}
                    sx={{ p: 0.1, color: frozen ? '#42a5f5' : 'grey.600' }}
                  >
                    <AcUnitIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell
              sx={{ ...gSx, width: 56, top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
              align="center"
              title="Open contracts held on the YES side (Poly on top, Kalshi below)."
            >
              YES
            </TableCell>
            <TableCell
              sx={{ ...gSx, width: 56, top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
              align="center"
              title="Open contracts held on the NO side (Poly on top, Kalshi below)."
            >
              NO
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={4}
            >
              Polymarket {polySideLabel}
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={4}
            >
              Kalshi {kalshiSideLabel}
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={3}
            >
              Parameters
            </TableCell>
            {/* Per-strategy groups — each spans 2 sub-cells: [legs] + [$/%].
                Legs show maker leg (blue) + taker leg (orange) on the two
                ACTUAL platforms the strategy uses, so the operator never has
                to mentally combine prices from different columns to read a
                strategy. Mirrors the unwinder's Sim-P-Mk / Sim-K-Mk layout
                (UnwindingTabSumOfSides). */}
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={2}
              title="Taker-Taker: both legs fire as taker (orange = all-in price each leg pays)."
            >
              TT
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={2}
              title="Poly maker: REST on Polymarket (blue) + TAKE on Kalshi (orange)."
            >
              P Mk
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={2}
              title="Kalshi maker: REST on Kalshi (blue) + TAKE on Polymarket (orange)."
            >
              K Mk
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={3}
            >
              Ctrl
            </TableCell>
          </TableRow>
          <TableRow>
            {/* Polymarket — Qty · Bid · Ask · Qty */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Bid</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Ask</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            {/* Kalshi — Qty · Bid · Ask · Qty */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Bid</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Ask</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            {/* Parameters */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 40, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Thr</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 30, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 36, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
            {/* Sim sub-row — per-strategy pair: [Legs placeholder] + [$/% sort].
                Legs cell has no sort handle; the $/% cell keeps the existing
                two-target sort UI ($ → sum, % → tna, arrow toggles direction).
                Strategy name (TT / P Mk / K Mk) lives in the group header row
                above, spanning both sub-cells. */}
            {(
              [
                { sumKey: 'sumTT',       tnaKey: 'tnaTT',       tip: 'Taker-Taker — click $ or % to switch sort target; arrow toggles direction.' },
                { sumKey: 'sumPolyMk',   tnaKey: 'tnaPolyMk',   tip: 'Poly maker — click $ or % to switch sort target; arrow toggles direction.' },
                { sumKey: 'sumKalshiMk', tnaKey: 'tnaKalshiMk', tip: 'Kalshi maker — click $ or % to switch sort target; arrow toggles direction.' },
              ] as const
            ).map(({ sumKey, tnaKey, tip }) => {
              const sumActive = sortKey === sumKey;
              const tnaActive = sortKey === tnaKey;
              const colActive = sumActive || tnaActive;
              const ArrowIcon = colActive && sortDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
              // Arrow click: toggle direction when this column is the
              // active sort; otherwise activate the $ sort for this
              // column (matches the default-direction logic in
              // handleSortClick).
              const arrowKey = tnaActive ? tnaKey : sumKey;
              return (
                <Fragment key={sumKey}>
                  {/* Legs placeholder — no sort. Width matches the body
                      legs cell below. */}
                  <TableCell sx={{ ...hSx, ...subHSx, width: 50, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} />
                  <TableCell
                    sx={{ ...hSx, ...subHSx, width: 50, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }}
                    align="right"
                    title={tip}
                  >
                    <Stack
                      direction="row"
                      spacing={0.2}
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <Box
                        component="span"
                        onClick={() => handleSortClick(sumKey)}
                        sx={sortBadgeSx(sumActive)}
                      >
                        $
                      </Box>
                      <ArrowIcon
                        onClick={() => handleSortClick(arrowKey)}
                        sx={{
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          color: colActive ? '#42a5f5' : 'inherit',
                          opacity: colActive ? 1 : 0.4,
                        }}
                      />
                      <Box
                        component="span"
                        onClick={() => handleSortClick(tnaKey)}
                        sx={sortBadgeSx(tnaActive)}
                      >
                        %
                      </Box>
                    </Stack>
                  </TableCell>
                </Fragment>
              );
            })}
            {/* Ctrl — 3 switches: PolyMk · KalshiMk · TT */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 28, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="center" title="Poly maker">P</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 28, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="center" title="Kalshi maker">K</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 28, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="center" title="Taker-Taker">TT</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(() => {
            const virtualItems = rowVirtualizer.getVirtualItems();
            const totalSize = rowVirtualizer.getTotalSize();
            const paddingTop =
              virtualItems.length > 0 ? virtualItems[0].start : 0;
            const paddingBottom =
              virtualItems.length > 0
                ? totalSize - virtualItems[virtualItems.length - 1].end
                : 0;
            // Spacer rows preserve the scrollbar size; `colSpan={100}` is
            // a known-safe "all columns" idiom — the browser clamps to
            // the actual count and the cell stays invisible.
            return (
              <>
                {paddingTop > 0 && (
                  <TableRow style={{ height: paddingTop }}>
                    <TableCell
                      colSpan={100}
                      sx={{ p: 0, border: 'none' }}
                      aria-hidden
                    />
                  </TableRow>
                )}
                {virtualItems.map(vi => {
                  const pair = displayedPairs[vi.index];
                  const rule = pair._id
                    ? rulesByPairId.get(pair._id)
                    : undefined;
                  const anyRule = pair._id
                    ? anyRulesByPairId.get(pair._id)
                    : undefined;
                  const liveState =
                    (pair._id ? getState(pair._id) : undefined) ??
                    (rule ? getState(rule._id) : undefined);
                  const lockedByOtherTab = pair._id
                    ? lockedPairIds.has(pair._id)
                    : false;
                  return (
                    <EntryRowByPath
                      key={pair._id ?? pair.pairName}
                      path={path}
                      pair={pair}
                      rule={rule}
                      anyRule={anyRule}
                      liveState={liveState}
                      lockedByOtherTab={lockedByOtherTab}
                      selected={pair._id ? selected.has(pair._id) : false}
                      onToggleSelect={toggleSelect}
                      contracts={
                        pair._id
                          ? contractsByPairId.get(pair._id) ?? ZERO_CONTRACTS
                          : ZERO_CONTRACTS
                      }
                      onCreateRule={onCreateRule}
                      onSetMakerFlag={onSetMakerFlag}
                      onUpdateRule={onUpdateRule}
                      onHidePair={onHidePair}
                    />
                  );
                })}
                {paddingBottom > 0 && (
                  <TableRow style={{ height: paddingBottom }}>
                    <TableCell
                      colSpan={100}
                      sx={{ p: 0, border: 'none' }}
                      aria-hidden
                    />
                  </TableRow>
                )}
              </>
            );
          })()}
        </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Row                                                                */
/* ------------------------------------------------------------------ */

interface RowProps {
  path: 'a' | 'b';
  pair: ManualConfirmedPair;
  rule?: EntryRule;
  anyRule?: EntryRule;
  liveState?: EnrichedEntryStatus;
  lockedByOtherTab: boolean;
  /** Whether this row's bulk-select checkbox is ticked. */
  selected: boolean;
  /** Toggle this pair in the bulk-select set. */
  onToggleSelect: (id: string) => void;
  contracts: PairContracts;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[],
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

const EntryRowByPath = memo(
  ({
    path,
    pair,
    rule,
    anyRule,
    liveState,
    lockedByOtherTab,
    selected,
    onToggleSelect,
    contracts,
    onCreateRule,
    onSetMakerFlag,
    onUpdateRule,
    onHidePair,
  }: RowProps) => {
    const ruleSize =
      rule?.kalshiConfig?.contractsToBuy ??
      rule?.polymarketConfig?.contractsToBuy ??
      DEFAULT_SIZE;
    const initThreshold =
      rule?.thresholdSum != null ? String(rule.thresholdSum) : String(DEFAULT_THRESHOLD);
    const initSize = String(ruleSize);
    const initMaxVol = String(rule?.maxVolumeContracts ?? DEFAULT_MAX_VOL);

    const [threshold, setThreshold] = useState(initThreshold);
    const [size, setSize] = useState(initSize);
    const [maxVol, setMaxVol] = useState(initMaxVol);
    const [savedThreshold, setSavedThreshold] = useState(initThreshold);
    const [savedSize, setSavedSize] = useState(initSize);
    const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
    const [thresholdError, setThresholdError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [togglingFlag, setTogglingFlag] = useState<MakerFlag | null>(null);
    const [togglingTT, setTogglingTT] = useState(false);
    const [hidePending, setHidePending] = useState(false);

    // Per-pair opener kill switch. Default OFF for manual-confirmed pairs;
    // the operator turns it on to start streaming WS data + arming rules.
    // No confirmation dialog here — turning off has no live position to
    // worry about (the opener never holds positions; entries settle into
    // the unwinder), unlike the unwinder side.
    const pairOpenerOn = liveState?.pairEnabled === true;
    const setPairOpenerEnabled = useSetPairOpenerEnabled();

    useEffect(() => {
      if (!rule) return;
      const dirty =
        threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;
      if (dirty) return;
      if (rule.thresholdSum != null) {
        const next = String(rule.thresholdSum);
        if (next !== savedThreshold) {
          setThreshold(next);
          setSavedThreshold(next);
        }
      }
      const ruleSizeNow =
        rule.kalshiConfig?.contractsToBuy ?? rule.polymarketConfig?.contractsToBuy;
      if (ruleSizeNow != null) {
        const next = String(ruleSizeNow);
        if (next !== savedSize) {
          setSize(next);
          setSavedSize(next);
        }
      }
      const nextMax = String(rule.maxVolumeContracts);
      if (nextMax !== savedMaxVol) {
        setMaxVol(nextMax);
        setSavedMaxVol(nextMax);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      rule?.thresholdSum,
      rule?.maxVolumeContracts,
      rule?.kalshiConfig?.contractsToBuy,
      rule?.polymarketConfig?.contractsToBuy,
    ]);

    const isDirty =
      threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;

    const validateThreshold = useCallback((value: string): string | null => {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n <= 0) return 'Must be > 0';
      if (n >= 1) return 'Must be < 1.00 (hard cap)';
      return null;
    }, []);

    useEffect(() => {
      setThresholdError(validateThreshold(threshold));
    }, [threshold, validateThreshold]);

    const handleSave = useCallback(async () => {
      if (!isDirty || !rule) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThreshold = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxVol = Number.parseFloat(maxVol);
      const dto: UpdateEntryRuleDto = {};
      if (
        Number.isFinite(parsedThreshold) &&
        parsedThreshold > 0 &&
        parsedThreshold < 1 &&
        threshold !== savedThreshold
      ) {
        dto.thresholdSum = parsedThreshold;
      }
      if (Number.isFinite(parsedMaxVol) && parsedMaxVol > 0 && maxVol !== savedMaxVol) {
        dto.maxVolumeContracts = parsedMaxVol;
      }
      if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
        if (rule.kalshiConfig) {
          dto.kalshiConfig = {
            side: rule.kalshiConfig.side,
            ticker: rule.kalshiConfig.ticker,
            contractsToBuy: parsedSize,
          };
        }
        if (rule.polymarketConfig) {
          dto.polymarketConfig = {
            side: rule.polymarketConfig.side,
            yesTokenId: rule.polymarketConfig.yesTokenId,
            noTokenId: rule.polymarketConfig.noTokenId,
            conditionId: rule.polymarketConfig.conditionId,
            contractsToBuy: parsedSize,
          };
        }
      }
      setSaving(true);
      const ok = await onUpdateRule(rule, dto);
      if (ok) {
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
      }
      setSaving(false);
    }, [
      isDirty,
      threshold,
      size,
      maxVol,
      savedThreshold,
      savedSize,
      savedMaxVol,
      rule,
      onUpdateRule,
      validateThreshold,
    ]);

    // Pre-rule commit: typing values with no rule creates a PAUSED rule
    // carrying those values. No maker flag is set — the row lands paused
    // with all 3 switches visually off (rule flags are all false on
    // creation). The operator later activates via a switch.
    const handleCreatePausedRule = useCallback(async () => {
      if (!isDirty || rule || !pair._id) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThr = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxV = Number.parseFloat(maxVol);
      if (!Number.isFinite(parsedThr) || parsedThr <= 0 || parsedThr >= 1) return;
      if (!Number.isFinite(parsedSize) || parsedSize <= 0) return;
      if (!Number.isFinite(parsedMaxV) || parsedMaxV <= 0) return;
      setSaving(true);
      try {
        await onCreateRule({
          pair,
          makerFlag: null,
          tabPath: path,
          thresholdSum: parsedThr,
          size: parsedSize,
          maxVolumeContracts: parsedMaxV,
        });
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
      } finally {
        setSaving(false);
      }
    }, [
      isDirty,
      rule,
      pair,
      path,
      threshold,
      size,
      maxVol,
      validateThreshold,
      onCreateRule,
    ]);

    const handleCommit = useCallback(async () => {
      if (rule) {
        await handleSave();
      } else {
        await handleCreatePausedRule();
      }
    }, [rule, handleSave, handleCreatePausedRule]);

    // Path of the existing rule (if any). Drives which switches are bound
    // to live state vs forced to off.
    const committedPath = liveState?.committedPath ?? null;
    const rulePathFromFlags: 'a' | 'b' | null = rule
      ? rule.polyYesMaker || rule.kalshiNoMaker
        ? 'a'
        : rule.polyNoMaker || rule.kalshiYesMaker
          ? 'b'
          : null
      : null;
    const rulePathFromSides: 'a' | 'b' | null = rule
      ? rule.polymarketConfig?.side === 'yes' && rule.kalshiConfig?.side === 'no'
        ? 'a'
        : rule.polymarketConfig?.side === 'no' && rule.kalshiConfig?.side === 'yes'
          ? 'b'
          : null
      : null;
    const rulePath: 'a' | 'b' | null =
      committedPath ?? rulePathFromFlags ?? rulePathFromSides;
    const ruleBelongsToThisTab = rulePath == null || rulePath === path;

    // Market data + math — always tracks the TAB's path, never a
    // "lower-sum" hint. The other tab owns the other path. All sim
    // numbers come straight from the backend `engineSim` payload.
    const metrics = computePathMetrics(path, liveState);
    const {
      polyAsk,
      polyBid,
      kalshiAsk,
      kalshiBid,
      polyAllIn,
      kalshiAllIn,
      polyMakerRest,
      kalshiMakerRest,
      sumTT,
      sumPolyMk,
      sumKalshiMk,
      tnaTT,
      tnaPolyMk,
      tnaKalshiMk,
    } = metrics;

    const polyAskSize =
      (path === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk)?.size;
    const polyBidSize =
      (path === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid)?.size;
    const kalshiAskSize =
      (path === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk)?.size;
    const kalshiBidSize =
      (path === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid)?.size;

    const thresholdNum = Number.parseFloat(threshold);
    const thresholdValid =
      Number.isFinite(thresholdNum) && thresholdNum > 0 && thresholdNum < 1;

    // Switch → flag mapping for this tab's path.
    const polyFlag = TAB_FLAGS[path].poly;
    const kalshiFlag = TAB_FLAGS[path].kalshi;
    const otherPath: 'a' | 'b' = path === 'a' ? 'b' : 'a';
    const otherPolyFlag = TAB_FLAGS[otherPath].poly;
    const otherKalshiFlag = TAB_FLAGS[otherPath].kalshi;

    // Switch display state: only bind to rule flags if the rule belongs
    // to this tab's path. Off otherwise — the OTHER tab owns it.
    const polyMakerOn = ruleBelongsToThisTab && rule?.[polyFlag] === true;
    const kalshiMakerOn = ruleBelongsToThisTab && rule?.[kalshiFlag] === true;
    const ttOn =
      ruleBelongsToThisTab &&
      (anyRule?.entryStrategy ?? 'maker-first') === 'taker-taker';

    const inSx = {
      '& .MuiInputBase-input': {
        fontSize: '0.62rem',
        py: 0.15,
        px: 0.2,
        textAlign: 'right',
        width: 28,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.08)',
      },
      minWidth: 0,
    } as const;

    const tooltipTitle = (
      <Box>
        <Typography sx={{ fontSize: '0.7rem' }}>
          {pair.polymarketTitle || pair.pairName}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          {pair.kalshiTitle || pair.pairName}
        </Typography>
      </Box>
    );

    // The red hard-cap row outline is gone. Hover highlight stays.
    const rowSx = { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } };

    // Apply a maker-flag change. Atomically clears the OTHER path's
    // flags when flipping ON, so a paused cross-path rule flips paths
    // cleanly without prompting the operator (the active-cross-path
    // lock has already disabled the switch by this point).
    const doSetMakerFlag = useCallback(
      async (flag: MakerFlag, value: boolean) => {
        setTogglingFlag(flag);
        try {
          if (rule) {
            const clearOthers: MakerFlag[] = value
              ? [otherPolyFlag, otherKalshiFlag]
              : [];
            await onSetMakerFlag(rule, flag, value, clearOthers);
          } else if (value) {
            const tErr = validateThreshold(threshold);
            if (tErr) {
              setThresholdError(tErr);
              return;
            }
            const parsedThr = Number.parseFloat(threshold);
            const parsedSize = Number.parseInt(size, 10);
            const parsedMaxV = Number.parseFloat(maxVol);
            await onCreateRule({
              pair,
              makerFlag: flag,
              tabPath: path,
              thresholdSum:
                Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                  ? parsedThr
                  : DEFAULT_THRESHOLD,
              size:
                Number.isFinite(parsedSize) && parsedSize > 0
                  ? parsedSize
                  : DEFAULT_SIZE,
              maxVolumeContracts:
                Number.isFinite(parsedMaxV) && parsedMaxV > 0
                  ? parsedMaxV
                  : DEFAULT_MAX_VOL,
            });
          }
        } finally {
          setTimeout(() => setTogglingFlag(null), 2000);
        }
      },
      [
        rule,
        threshold,
        size,
        maxVol,
        pair,
        path,
        otherPolyFlag,
        otherKalshiFlag,
        onCreateRule,
        onSetMakerFlag,
        validateThreshold,
      ],
    );

    const handleTTToggle = useCallback(
      async (value: boolean) => {
        if (lockedByOtherTab) return;
        setTogglingTT(true);
        try {
          if (anyRule) {
            // If the rule belongs to the OTHER tab (paused, not
            // committed), we'd need a comprehensive PATCH to switch
            // sides+clear-flags+set-entryStrategy. The lock set already
            // forbids that for active rules. For paused cross-path
            // rules, we send a side-flipping PATCH on TT-on so the
            // operator can switch paths via TT without an extra step.
            if (!ruleBelongsToThisTab && value) {
              const sides = FLAG_TO_SIDES[polyFlag];
              const dto: UpdateEntryRuleDto = {
                entryStrategy: 'taker-taker',
                [otherPolyFlag]: false,
                [otherKalshiFlag]: false,
              };
              if (anyRule.kalshiConfig) {
                dto.kalshiConfig = {
                  side: sides.kalshiSide,
                  ticker: anyRule.kalshiConfig.ticker,
                  contractsToBuy: anyRule.kalshiConfig.contractsToBuy,
                };
              }
              if (anyRule.polymarketConfig) {
                dto.polymarketConfig = {
                  side: sides.polySide,
                  yesTokenId: anyRule.polymarketConfig.yesTokenId,
                  noTokenId: anyRule.polymarketConfig.noTokenId,
                  conditionId: anyRule.polymarketConfig.conditionId,
                  contractsToBuy: anyRule.polymarketConfig.contractsToBuy,
                };
              }
              await onUpdateRule(anyRule, dto);
            } else {
              await onUpdateRule(anyRule, {
                entryStrategy: value ? 'taker-taker' : 'maker-first',
              });
            }
            return;
          }
          if (!value) return;
          const tErr = validateThreshold(threshold);
          if (tErr) {
            setThresholdError(tErr);
            return;
          }
          const parsedThr = Number.parseFloat(threshold);
          const parsedSize = Number.parseInt(size, 10);
          const parsedMaxV = Number.parseFloat(maxVol);
          await onCreateRule({
            pair,
            makerFlag: null,
            tabPath: path,
            entryStrategy: 'taker-taker',
            thresholdSum:
              Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                ? parsedThr
                : DEFAULT_THRESHOLD,
            size:
              Number.isFinite(parsedSize) && parsedSize > 0
                ? parsedSize
                : DEFAULT_SIZE,
            maxVolumeContracts:
              Number.isFinite(parsedMaxV) && parsedMaxV > 0
                ? parsedMaxV
                : DEFAULT_MAX_VOL,
          });
        } finally {
          setTimeout(() => setTogglingTT(false), 2000);
        }
      },
      [
        anyRule,
        lockedByOtherTab,
        ruleBelongsToThisTab,
        polyFlag,
        otherPolyFlag,
        otherKalshiFlag,
        pair,
        path,
        threshold,
        size,
        maxVol,
        onCreateRule,
        onUpdateRule,
        validateThreshold,
      ],
    );

    const ruleStatusColor =
      anyRule?.status === 'active'
        ? '#4caf50'
        : anyRule?.status === 'paused'
          ? '#ff9800'
          : 'grey.600';

    // Sum cell color: red if over hard cap (informational only — the
    // backend gate is the safety contract); green if at or below the
    // operator's threshold; default blue otherwise.
    const sumColor = (sum: number | null): string => {
      if (sum == null) return 'grey.500';
      if (sum >= ENTRY_HARD_CAP_GROSS) return '#f44336';
      if (thresholdValid && sum <= thresholdNum) return '#4caf50';
      return '#42a5f5';
    };
    const tnaColor = (tna: number | null): string => {
      if (tna == null) return 'grey.500';
      return tna >= 0 ? '#4caf50' : '#f44336';
    };

    const lockTooltip = lockedByOtherTab
      ? `Locked — same pair has an active rule on the ${
          path === 'a' ? 'B (poly NO + kalshi YES)' : 'A (poly YES + kalshi NO)'
        } path. Switch tabs to manage it.`
      : '';

    const polyCategoryFeePct = (
      polymarketTakerFeeRate(rule?.category) * 100
    ).toFixed(1);

    return (
      <>
        <TableRow sx={rowSx}>
          {/* Bulk select — disabled for cross-path-locked pairs so a bulk
              action never targets a pair this tab can't manage. */}
          <TableCell
            sx={{ ...cSx, px: 0, verticalAlign: 'middle' }}
            align="center"
            padding="none"
            onClick={e => e.stopPropagation()}
          >
            <Checkbox
              size="small"
              checked={selected}
              disabled={!pair._id || lockedByOtherTab}
              onChange={() => pair._id && onToggleSelect(pair._id)}
              sx={{ p: 0, color: 'grey.600', '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </TableCell>
          {/* Market */}
          <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
              <Tooltip
                title={
                  anyRule
                    ? `Entry rule: ${anyRule.status}`
                    : 'No rule yet — toggle a switch to create'
                }
                placement="top"
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: ruleStatusColor,
                    mt: 0.5,
                    ml: 0.2,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  pairOpenerOn
                    ? 'Pair ON — opener is streaming market data and rules can be activated.'
                    : 'Pair OFF — no market data, rules cannot be activated. Click to turn on.'
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={pairOpenerOn}
                    disabled={setPairOpenerEnabled.isLoading || !pair._id}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (!pair._id) return;
                      setPairOpenerEnabled.mutate({
                        pairId: pair._id,
                        enabled: e.target.checked,
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disableRipple
                    sx={{
                      m: 0,
                      p: 0,
                      width: 18,
                      height: 10,
                      display: 'inline-flex',
                      '& .MuiSwitch-switchBase': {
                        p: '1px',
                        '&.Mui-checked': {
                          transform: 'translateX(8px)',
                          color: '#fff',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#7c3aed',
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiSwitch-thumb': {
                        width: 8,
                        height: 8,
                        boxShadow: 'none',
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 5,
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        opacity: 1,
                      },
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Remove pair from opener page" placement="top">
                <span>
                  <IconButton
                    size="small"
                    disabled={hidePending}
                    onClick={async e => {
                      e.stopPropagation();
                      setHidePending(true);
                      try {
                        await onHidePair(pair);
                      } catch {
                        // The mutation's onError rolls the optimistic cache
                        // back; nothing else to do here.
                      } finally {
                        setHidePending(false);
                      }
                    }}
                    sx={{
                      p: 0,
                      ml: 0.4,
                      color: hidePending ? 'grey.700' : 'grey.500',
                      '&:hover': { color: '#f44336' },
                      flexShrink: 0,
                    }}
                  >
                    {hidePending ? (
                      <CircularProgress size={12} />
                    ) : (
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={tooltipTitle} placement="top-start">
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.15,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <a
                      href={
                        pair.polymarket?.marketUrl ??
                        liveState?.polymarketMarketUrl ??
                        undefined
                      }
                      target="_blank"
                      rel="noopener"
                      onClick={e => e.stopPropagation()}
                    >
                      <img
                        src="/assets/exchanges/png/100x100/polymarket.png"
                        alt="P"
                        style={{ width: 13, height: 13, borderRadius: '50%' }}
                      />
                    </a>
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pair.polymarketTitle || pair.pairName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <a
                      href={
                        pair.kalshi?.marketUrl ??
                        liveState?.kalshiMarketUrl ??
                        undefined
                      }
                      target="_blank"
                      rel="noopener"
                      onClick={e => e.stopPropagation()}
                    >
                      <img
                        src="/assets/exchanges/png/100x100/kalshi.png"
                        alt="K"
                        style={{ width: 13, height: 13, borderRadius: '50%' }}
                      />
                    </a>
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        color: 'grey.400',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pair.kalshiTitle || pair.pairName}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </TableCell>

          {/* Position contracts split by side, with Poly on top and
              Kalshi below in each cell. Sourced from current OPEN
              prediction_positions so a flip (e.g. Poly YES closes and
              Poly NO opens) visibly moves contracts between columns. */}
          <TableCell sx={cSx} align="center">
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.polyYes ? 'grey.200' : 'grey.600',
                }}
              >
                P: {fmtContracts(contracts.polyYes)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.kalshiYes ? 'grey.300' : 'grey.600',
                }}
              >
                K: {fmtContracts(contracts.kalshiYes)}
              </Typography>
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="center">
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.polyNo ? 'grey.200' : 'grey.600',
                }}
              >
                P: {fmtContracts(contracts.polyNo)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contracts.kalshiNo ? 'grey.300' : 'grey.600',
                }}
              >
                K: {fmtContracts(contracts.kalshiNo)}
              </Typography>
            </Stack>
          </TableCell>

          {/* Polymarket — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>
              {fmtPrice(polyBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {fmtPrice(polyAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyAskSize)}
            </Typography>
          </TableCell>

          {/* Kalshi — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>
              {fmtPrice(kalshiBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {fmtPrice(kalshiAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiAskSize)}
            </Typography>
          </TableCell>

          {/* Parameters */}
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Tooltip
              title={thresholdError ?? ''}
              placement="top"
              disableHoverListener={!thresholdError}
            >
              <span>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0}
                  justifyContent="flex-end"
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    value={threshold}
                    placeholder="0.95"
                    onChange={e => setThreshold(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCommit();
                    }}
                    error={!!thresholdError}
                    helperText={thresholdError ?? ''}
                    sx={inSx}
                    inputProps={{ step: 0.01, min: 0, max: 0.999 }}
                  />
                  {renderSaveIndicator(
                    saving,
                    threshold !== savedThreshold,
                    handleCommit,
                  )}
                </Stack>
              </span>
            </Tooltip>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={size}
                placeholder="50"
                onChange={e => setSize(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                sx={inSx}
                inputProps={{ step: 1, min: 1 }}
              />
              {renderSaveIndicator(saving, size !== savedSize, handleCommit)}
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={maxVol}
                placeholder="500"
                onChange={e => setMaxVol(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                sx={inSx}
                inputProps={{ step: 100, min: 0 }}
              />
              {renderSaveIndicator(saving, maxVol !== savedMaxVol, handleCommit)}
            </Stack>
          </TableCell>

          {/* Per-strategy Sim group — each strategy renders 2 cells:
              [LEGS]: stacked maker (blue) + taker (orange) prices,
                      each tagged with the platform letter (P/K) so
                      the operator never has to guess which platform a
                      number applies to. TT has no maker — both legs
                      are taker (both orange).
              [SUM/TNA]: existing sum + TNA% (unchanged).

              Mirrors the unwinder's per-strategy 2-cell layout
              (UnwindingTabSumOfSides) so reading a strategy is a
              single visual span instead of cross-column assembly. */}
          {(() => {
            // All four notes describe the backend `engineSim` payload —
            // there is no client-estimate path anymore.
            const engineSize = liveState?.engineSim?.sizeUsed;
            const polyTakerNote =
              engineSize != null ? `at size ${engineSize} (depth-walked VWAP)` : '';
            const kalshiTakerNote =
              engineSize != null ? `at size ${engineSize} (depth-walked VWAP)` : '';
            const polyMakerNote =
              'engine placement price (floor(threshold − kalshi_taker, 0.001))';
            const kalshiMakerNote =
              'engine placement price (floor(threshold − poly_taker, 0.001))';

            // Style helpers for the maker (blue) + taker (orange) cell
            // contents. The platform letter (P / K) is rendered as a
            // subscript-style chip after the price so the operator can
            // tell at a glance which platform a number applies to.
            const takerSx = {
              fontSize: '0.7rem',
              color: '#ff9800',
              cursor: 'help',
            } as const;
            const makerSx = {
              fontSize: '0.7rem',
              color: '#42a5f5',
              fontWeight: 600,
              cursor: 'help',
            } as const;
            const platformBadgeSx = {
              fontSize: '0.55rem',
              color: 'grey.500',
              fontWeight: 400,
              ml: 0.25,
            } as const;

            const polyTakerTooltip =
              polyAsk == null
                ? ''
                : `Polymarket taker — top-of-book ask: ${fmtPrice(polyAsk)}. All-in ${polyTakerNote}: ${fmtPrice(polyAllIn)} (Poly fee ${polyCategoryFeePct}%).`;
            const kalshiTakerTooltip =
              kalshiAsk == null
                ? ''
                : `Kalshi taker — top-of-book ask: ${fmtPrice(kalshiAsk)}. All-in ${kalshiTakerNote}: ${fmtPrice(kalshiAllIn)} (Kalshi fee ${(KALSHI_TAKER_FEE_RATE * 100).toFixed(1)}%).`;
            const polyMakerTooltip = `Polymarket maker rest: ${fmtPrice(polyMakerRest)} — ${polyMakerNote}.`;
            const kalshiMakerTooltip = `Kalshi maker rest: ${fmtPrice(kalshiMakerRest)} — ${kalshiMakerNote}.`;

            // Each strategy: legs cell content + sum + tna. `topLeg` /
            // `bottomLeg` describe the two prices to render, top to
            // bottom in the legs cell. For maker-taker strategies the
            // TAKER (orange) sits on top and the MAKER (blue) below, so
            // every cell reads the same way: the leg we cross first up
            // top, our resting leg beneath it.
            type Leg = {
              price: number | null;
              raw: number | null;
              platform: 'P' | 'K';
              sx: Record<string, unknown>;
              tooltip: string;
            };
            const strategies: Array<{
              key: string;
              tip: string;
              sum: number | null;
              tna: number | null;
              topLeg: Leg;
              bottomLeg: Leg;
            }> = [
              {
                key: 'tt',
                tip: 'Taker-Taker',
                sum: sumTT,
                tna: tnaTT,
                topLeg: {
                  price: polyAllIn,
                  raw: polyAsk,
                  platform: 'P',
                  sx: takerSx,
                  tooltip: polyTakerTooltip,
                },
                bottomLeg: {
                  price: kalshiAllIn,
                  raw: kalshiAsk,
                  platform: 'K',
                  sx: takerSx,
                  tooltip: kalshiTakerTooltip,
                },
              },
              {
                key: 'polyMk',
                tip: 'Poly maker',
                sum: sumPolyMk,
                tna: tnaPolyMk,
                // Taker (Kalshi) on top, maker (Poly) below.
                topLeg: {
                  price: kalshiAllIn,
                  raw: kalshiAsk,
                  platform: 'K',
                  sx: takerSx,
                  tooltip: kalshiTakerTooltip,
                },
                bottomLeg: {
                  price: polyMakerRest,
                  raw: polyMakerRest,
                  platform: 'P',
                  sx: makerSx,
                  tooltip: polyMakerTooltip,
                },
              },
              {
                key: 'kalshiMk',
                tip: 'Kalshi maker',
                sum: sumKalshiMk,
                tna: tnaKalshiMk,
                // Taker (Poly) on top, maker (Kalshi) below.
                topLeg: {
                  price: polyAllIn,
                  raw: polyAsk,
                  platform: 'P',
                  sx: takerSx,
                  tooltip: polyTakerTooltip,
                },
                bottomLeg: {
                  price: kalshiMakerRest,
                  raw: kalshiMakerRest,
                  platform: 'K',
                  sx: makerSx,
                  tooltip: kalshiMakerTooltip,
                },
              },
            ];

            const renderLeg = (leg: Leg) => (
              <Tooltip
                placement="top"
                title={leg.tooltip}
                disableHoverListener={leg.raw == null}
              >
                <Stack direction="row" alignItems="baseline" spacing={0} justifyContent="flex-end">
                  <Typography sx={leg.sx}>{fmtPrice(leg.price)}</Typography>
                  <Typography component="span" sx={platformBadgeSx}>{leg.platform}</Typography>
                </Stack>
              </Tooltip>
            );

            return (
              <>
                {strategies.map(({ key, tip, sum, tna, topLeg, bottomLeg }) => (
                  <Fragment key={key}>
                    {/* Legs */}
                    <TableCell sx={cSx} align="right">
                      <Stack spacing={0} alignItems="flex-end">
                        {renderLeg(topLeg)}
                        {renderLeg(bottomLeg)}
                      </Stack>
                    </TableCell>
                    {/* Sum + TNA */}
                    <TableCell sx={cSx} align="right">
                      <Tooltip title={tip} placement="top">
                        <Stack spacing={0} alignItems="flex-end">
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              color: sumColor(sum),
                            }}
                          >
                            {fmtPrice(sum)}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.62rem', color: tnaColor(tna) }}
                          >
                            {fmtPct(tna)}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  </Fragment>
                ))}
              </>
            );
          })()}

          {/* Ctrl — Poly maker · Kalshi maker · TT */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {togglingFlag === polyFlag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  lockedByOtherTab
                    ? lockTooltip
                    : `Poly ${path === 'a' ? 'YES' : 'NO'} as maker`
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={polyMakerOn}
                    disabled={lockedByOtherTab}
                    onChange={(_, v) => void doSetMakerFlag(polyFlag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {togglingFlag === kalshiFlag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  lockedByOtherTab
                    ? lockTooltip
                    : `Kalshi ${path === 'a' ? 'NO' : 'YES'} as maker`
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={kalshiMakerOn}
                    disabled={lockedByOtherTab}
                    onChange={(_, v) => void doSetMakerFlag(kalshiFlag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {anyRule && (anyRule.balancingContractsRemaining ?? 0) > 0 ? (
              <Tooltip
                title={`Balancing: ${anyRule.balancingContractsRemaining} contracts resting on ${anyRule.balancingLeg}`}
                placement="top"
              >
                <CircularProgress size={14} />
              </Tooltip>
            ) : togglingTT ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  lockedByOtherTab
                    ? lockTooltip
                    : ttOn
                      ? 'Taker-Taker on. Click to switch off.'
                      : 'Taker-Taker: fire BOTH legs as parallel takers when combined cost clears threshold.'
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={ttOn}
                    disabled={lockedByOtherTab}
                    onChange={(_, v) => void handleTTToggle(v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

      </>
    );
  },
  (prev, next) =>
    prev.path === next.path &&
    prev.pair === next.pair &&
    prev.rule === next.rule &&
    prev.anyRule === next.anyRule &&
    prev.liveState === next.liveState &&
    prev.lockedByOtherTab === next.lockedByOtherTab &&
    prev.selected === next.selected &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onCreateRule === next.onCreateRule &&
    prev.onSetMakerFlag === next.onSetMakerFlag &&
    prev.onUpdateRule === next.onUpdateRule &&
    prev.onHidePair === next.onHidePair,
);
